import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FormGroup } from 'react-bootstrap';
import { cloneDeep, set, get, isArray, concat, last, flatMap } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MentionsInput, Mention } from 'react-mentions';
import {
  productListLocationsArray,
  calculationProductListSubLocationsArray,
  checValidMongoId,
  calculationBtnArrays,
  splitAndFilterString,
  handleSubLocation,
  RightsAccess,
} from '../../../../Components/helper';
import { useCustomColumnState } from '../CustomColumnContextProvider';
import { calculationFormulaPath, customColumnPath } from '../fieldPath';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { useCostingAccess } from '../../../../Components/CustomHooks/useCostingAccess';

function CalculationComponent({ data, pIndex, formulaArry, isEditMode, allowEditFormula, setFormulaHasError, setAllowEditFormula, formulaHasError }) {
  const locationRef = useRef(null);
  const subLocationRef = useRef(null);
  const { t } = useTranslation('common');
  const { TestingRight } = RightsAccess();
  const { customColumnState, setCustomColumnState, converDropdownData, globalError } = useCustomColumnState();
  const reduxData = useSelector((prevState) => prevState);
  const {
    customSectionData: { dynamicColArr, measurementArr, sampleTrackingDates, currSectionList },
  } = reduxData;

  const hasCostingAccess = useCostingAccess();

  const calculationLocationOptions = useMemo(
    () => productListLocationsArray.filter((item) => item?.name !== 'Design_Details' && (hasCostingAccess || item?.name !== 'Costing_Information')),
    [hasCostingAccess],
  );

  const [state, setState] = useState({
    currLocation: {
      label: 'Product Core Information',
      name: 'Product_Core_Information',
      _id: 11,
    },
    currSubLocation: null,
    counter: 0,
    locationArray: calculationLocationOptions,
    filteredLocationArray: calculationLocationOptions,
    sublocationArray: null,
    filteredSubLocationArray: null,
  });
  const [undo, setUndo] = useState([]);
  const [redo, setRedo] = useState([]);
  const [childOptions, setChildOptions] = useState([]);
  const { counter, currLocation, locationArray, filteredLocationArray, sublocationArray, filteredSubLocationArray } = state;

  /**
   * Rebuilds the location lists from the rights-filtered options plus the custom sections, so a
   * rights change or a newly loaded section list never leaves a stale or duplicated entry behind.
   */
  useEffect(() => {
    const rightsFilteredLocations = TestingRight ? calculationLocationOptions : calculationLocationOptions.filter((item) => item.name !== 'Testing_Information');
    const sectionLocations = isArray(currSectionList) ? currSectionList.map((section) => ({ label: section?.name, name: section?._id })) : [];
    const nextLocations = [...rightsFilteredLocations, ...sectionLocations];
    setState((prev) => ({
      ...prev,
      filteredLocationArray: nextLocations,
      locationArray: nextLocations,
    }));
  }, [calculationLocationOptions, TestingRight, currSectionList]);

  useEffect(() => {
    if (state?.locationArray?.length > 0) {
      const opArray = [];
      state?.locationArray?.forEach((val) => {
        const getSectionsColumns = cloneDeep(currSectionList?.find((ele) => val?.name === ele?._id));
        const locationValue = cloneDeep(calculationProductListSubLocationsArray?.find((subLocationArr) => val?._id === subLocationArr?.locationId));
        if (locationValue) {
          locationValue?.subLocations?.forEach((subEle) => {
            opArray.push({ id: `${subEle?.label}_(${val?.label})`, display: `${subEle?.label}_(${val?.label})` });
          });
        } else if (getSectionsColumns && isArray(getSectionsColumns?.columns) && getSectionsColumns?.columns.length > 0) {
          getSectionsColumns?.columns?.forEach((dyCol) => {
            opArray.push({ id: `${dyCol?.title}_(${val?.label})`, display: `${dyCol?.title}_(${val?.label})` });
          });
        }
      });
      setChildOptions(opArray);
    }
  }, [state, currSectionList, calculationProductListSubLocationsArray]);

  const setSubLocationArr = () => {
    let subLocationArr = cloneDeep(calculationProductListSubLocationsArray?.find((val) => val?.locationId === state?.currLocation?._id) || {});
    let manipulatedDynamicColumns = converDropdownData(dynamicColArr);
    manipulatedDynamicColumns = manipulatedDynamicColumns?.map((dyCol) => {
      return {
        label: dyCol?.keyName,
        name: dyCol?._id,
        type: 'string',
      };
    });
    const manipulatedMeasurmentArr = measurementArr.map((dyCol) => {
      return {
        label: dyCol?.name,
        name: dyCol?._id,
        type: 'number',
      };
    });
    const sampleTrackingArr = sampleTrackingDates.map((dyCol) => {
      return {
        label: dyCol?.name,
        name: dyCol?._id,
        type: 'string',
      };
    });
    if (state?.currLocation?.name === 'Product_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, manipulatedDynamicColumns);
    }

    if (state?.currLocation?.name === 'Measurement_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, manipulatedMeasurmentArr);
    }
    if (state?.currLocation?.name === 'Sample_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, sampleTrackingArr);
    }
    if (checValidMongoId(state?.currLocation?.name)) {
      const getSectionsColumns = cloneDeep(currSectionList?.find((val) => val?._id === state?.currLocation?.name));
      const subLocationObj = {};
      if (getSectionsColumns && isArray(getSectionsColumns?.columns) && getSectionsColumns?.columns.length > 0) {
        const newSubColumns = getSectionsColumns?.columns?.map((dyCol) => {
          const obj = {
            label: dyCol?.title,
          };
          if (dyCol?.columnType === 'calculation') {
            obj.name = dyCol?.formulaArr;
            obj.type = 'number';
          } else if (dyCol?.columnType === 'source') {
            obj.name = [dyCol?.source];
            obj.type = 'number';
          } else if (dyCol?.columnType === 'manual') {
            obj.name = dyCol?._id;
            obj.type = 'manualNumber';
            obj.numberType = dyCol?.key;
          }
          return obj;
        });

        subLocationObj.locationId = state?.currLocation?.name;
        subLocationObj.subLocations = newSubColumns;
      }
      subLocationArr = subLocationObj;
      return subLocationArr;
    }
    return subLocationArr;
  };

  useMemo(() => {
    const subLocate = setSubLocationArr();
    setState((prev) => ({
      ...prev,
      filteredSubLocationArray: subLocate.subLocations,
      sublocationArray: subLocate.subLocations,
    }));
    return subLocate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.currLocation?.name]);

  const updateFormulastring = (formulaArrys, copyState) => {
    let getCurrFormulaString = '';
    let readOnlyFormulaString = '';

    formulaArrys?.forEach((formu) => {
      getCurrFormulaString = getCurrFormulaString.concat(formu?.subLocation?.name, '');
      readOnlyFormulaString = readOnlyFormulaString.concat(formu?.subLocation?.label, '');
    });
    set(copyState, calculationFormulaPath(pIndex).FORMULA_STRING, getCurrFormulaString);
    set(copyState, calculationFormulaPath(pIndex).READONLY_FORMULA_STRING, readOnlyFormulaString);
    return copyState;
  };

  const onLocationChange = (value, keyName) => {
    let copyState = customColumnState;
    let clonedFormulaArray = get(copyState, calculationFormulaPath(pIndex).FORMULA_ARRAY, []);
    if (keyName === 'location') {
      setState((prev) => ({
        ...prev,
        counter: 0,
        currSubLocation: null,
        currLocation: value,
      }));
    } else if (keyName === 'subLocation' && counter === 0 && isArray(value?.name)) {
      clonedFormulaArray = concat(clonedFormulaArray, value?.name);
      setState((prev) => ({ ...prev, counter: 1, currSubLocation: value }));
    } else if (keyName === 'subLocation' && counter === 0) {
      const obj = {
        location: currLocation,
        subLocation: value,
      };
      clonedFormulaArray.push(obj);
      setState((prev) => ({ ...prev, counter: 1, currSubLocation: value }));
    } else if (value?.type === 'number' || value?.type === 'Number' || value?.type === 'Number_percentage') {
      const obj = {
        location: value,
        subLocation: value,
      };
      clonedFormulaArray.push(obj);
      setState((prev) => ({
        ...prev,
        counter: 1,
        currSubLocation: null,
      }));
    } else if (keyName === 'calcSymbol' && ['(', ')', '%'].includes(value?.name)) {
      const obj = {
        location: value,
        subLocation: value,
      };
      clonedFormulaArray.push(obj);
      if (value?.name === ')') {
        setState((prev) => ({
          ...prev,
          counter: 1,
          currSubLocation: null,
        }));
      }
    } else if (keyName === 'calcSymbol' && counter === 1) {
      const obj = {
        location: value,
        subLocation: value,
      };
      clonedFormulaArray.push(obj);
      setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
    } else if (counter === 0) {
      toasterMsg(t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.SEARCH_SUB_LOCATIONS'), 'info');
    } else {
      toasterMsg(t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.SELECT_SYMBOLS'), 'info');
    }
    const foundString = clonedFormulaArray?.filter((val) => val?.subLocation?.type === 'string');
    const checkFormulaHasStringType = foundString?.length > 0;
    if (checkFormulaHasStringType) {
      clonedFormulaArray = clonedFormulaArray
        .filter((val) => !['(', ')', '%'].includes(val?.subLocation?.name))
        .map((formu) => {
          if (formu?.subLocation?.type === 'symbol') {
            formu = {
              location: { name: '&', label: '&', type: 'symbol' },
              subLocation: { name: '&', label: '&', type: 'symbol' },
            };
          }
          return formu;
        });
      copyState = updateFormulastring(clonedFormulaArray, copyState);
    }
    set(copyState, calculationFormulaPath(pIndex).FORMULA_ARRAY, clonedFormulaArray);
    setUndo([...undo, ...clonedFormulaArray]);
    setRedo([]);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const clearFormula = () => {
    const copyState = customColumnState;
    set(copyState, calculationFormulaPath(pIndex).FORMULA_ARRAY, []);
    set(copyState, calculationFormulaPath(pIndex).FORMULA_STRING, '');
    set(copyState, calculationFormulaPath(pIndex).READONLY_FORMULA_STRING, '');
    set(copyState, calculationFormulaPath(pIndex).READONLY_FORMULA_STRING, '');
    setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
    setUndo([]);
    setRedo([]);
  };

  useEffect(() => {
    if (isArray(formulaArry)) {
      let copyState = customColumnState;
      copyState = updateFormulastring(formulaArry, copyState);
      setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
      setUndo([...undo, ...formulaArry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaArry?.length, pIndex]);

  useEffect(() => {
    if (isEditMode) {
      const lastObj = last(formulaArry);
      const obj = { counter: 0 };
      if (
        [')'].includes(lastObj?.subLocation?.name) ||
        ['number', 'Number', 'Number_percentage'].includes(lastObj?.subLocation?.type) ||
        lastObj?.subLocation?.type === 'string' ||
        (lastObj?.subLocation?.type === 'symbol' && lastObj?.subLocation?.name === '%')
      ) {
        obj.counter = 1;
      } else if (lastObj?.subLocation?.type === 'symbol') {
        obj.counter = 0;
      }
      setState((prev) => ({
        ...prev,
        counter: obj?.counter,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, formulaArry?.length]);

  const undoFormula = () => {
    const copyState = customColumnState;
    if (isArray(formulaArry)) {
      let copyFormulaArr = cloneDeep(get(copyState, customColumnPath(pIndex).CALCULATION_DEFAULT_ARR, []));
      const lastPopObject = copyFormulaArr[copyFormulaArr.length - 1];
      const cloneRedoArr = [...redo];
      if (lastPopObject) {
        cloneRedoArr.push(lastPopObject);
      }
      copyFormulaArr = copyFormulaArr.slice(0, -1);
      const getObj = copyFormulaArr[copyFormulaArr.length - 1];
      if (getObj?.subLocation?.type === 'symbol') {
        setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
      } else if (getObj) setState((prev) => ({ ...prev, counter: 1, currSubLocation: null }));
      else {
        setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
      }
      set(copyState, customColumnPath(pIndex).CALCULATION_DEFAULT_ARR, copyFormulaArr);
      setRedo(cloneRedoArr);
      setUndo(copyFormulaArr);
      setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
    }
  };

  const redoFormula = () => {
    const copyState = customColumnState;
    let cloneRedo = [...redo];
    const copyFormulaArr = cloneDeep(get(copyState, customColumnPath(pIndex).CALCULATION_DEFAULT_ARR, []));
    copyFormulaArr.push(cloneRedo[cloneRedo.length - 1]);
    cloneRedo = cloneRedo.slice(0, -1);
    const getObj = copyFormulaArr[copyFormulaArr.length - 1];
    if (getObj?.subLocation?.type === 'symbol') {
      setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
    } else if (getObj) setState((prev) => ({ ...prev, counter: 1, currSubLocation: null }));
    else {
      setState((prev) => ({ ...prev, counter: 0, currSubLocation: null }));
    }
    setRedo(cloneRedo);
    set(copyState, customColumnPath(pIndex).CALCULATION_DEFAULT_ARR, copyFormulaArr);
    setUndo(copyFormulaArr);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const checkFormulaHasPercentage = () => {
    const lastObj = last(formulaArry);
    return lastObj?.subLocation?.name === '%';
  };

  const foundString = formulaArry?.filter((val) => val?.subLocation?.type === 'string');
  const checkFormulaHasStringType = foundString?.length > 0;
  const counterState = counter === 0;
  const checkFormulaHasPercentageAtlast = useMemo(() => {
    return checkFormulaHasPercentage();
  }, [formulaArry?.length]);
  const calculationBtnArr = calculationBtnArrays(checkFormulaHasStringType, counterState, checkFormulaHasPercentageAtlast);

  const handleFilter = (val, filterBy) => {
    if (filterBy === 'location') {
      const filteredData = locationArray.filter((location) => location.label.toLowerCase().includes(val.toLowerCase()));
      setState((prev) => ({
        ...prev,
        filteredLocationArray: filteredData,
      }));
    } else if (filterBy === 'sublocation') {
      const filteredData = sublocationArray.filter((location) => location.label.toLowerCase().includes(val.toLowerCase()));
      setState((prev) => ({
        ...prev,
        filteredSubLocationArray: filteredData,
      }));
    }
  };

  const clearLocation = (type) => {
    if (type === 'location') {
      locationRef.current.value = '';
      setState((prev) => ({
        ...prev,
        filteredLocationArray: locationArray,
      }));
    } else if (type === 'sublocation') {
      subLocationRef.current.value = '';
      setState((prev) => ({
        ...prev,
        filteredSubLocationArray: sublocationArray,
      }));
    }
  };

  const createNewSubColumns = () => {
    const getSectionsColumns = flatMap(cloneDeep(currSectionList?.map((dyCol) => dyCol?.columns)));
    return getSectionsColumns?.map((dyCol) => {
      const obj = {
        label: dyCol?.title,
      };
      if (dyCol?.columnType === 'calculation') {
        obj.name = dyCol?.formulaArr;
        obj.type = 'number';
      } else if (dyCol?.columnType === 'source') {
        obj.name = [dyCol?.source];
        obj.type = 'number';
      } else if (dyCol?.columnType === 'manual') {
        obj.name = dyCol?._id;
        obj.type = 'manualNumber';
        obj.numberType = dyCol?.key;
      }
      return obj;
    });
  };

  const handleResultValue = (result, newFormulaArray) => {
    let hasError = false;
    result?.forEach((ele) => {
      const characters = calculationBtnArr?.find((val) => val?.name === ele);
      const relation = state?.filteredSubLocationArray?.find((val) => val?.label === ele);
      const subLocationValue = handleSubLocation(ele);
      const newSubColumns = createNewSubColumns();
      const foundSubColumn = newSubColumns?.find((val) => val?.label === ele);
      if (characters) {
        newFormulaArray.push({ location: characters, subLocation: characters });
      } else if (relation) {
        newFormulaArray.push({ location: relation, subLocation: relation });
      } else if (subLocationValue) {
        newFormulaArray.push(subLocationValue);
      } else if (parseFloat(ele)) {
        const splitDigits = parseFloat(ele)?.toString()?.split('');
        if (splitDigits?.length > 0) {
          hasError = handleResultValue(splitDigits, newFormulaArray);
        }
      } else if (foundSubColumn) {
        newFormulaArray.push(...(foundSubColumn?.name || []));
      } else {
        hasError = true;
      }
    });
    return hasError;
  };

  const handleNewFormulaArray = (newFormulaArray) => {
    let copyState = cloneDeep(customColumnState);
    const hasString = newFormulaArray?.find((val) => val?.subLocation?.type === 'string');
    let finalNewFormulaArray = newFormulaArray;
    if (hasString) {
      const filteredArr = newFormulaArray?.filter((val) => !['(', ')', '%'].includes(val?.subLocation?.name));
      finalNewFormulaArray = filteredArr?.map((formulaELe) => {
        if (['+', '-', '*', '/'].includes(formulaELe?.subLocation?.name)) {
          return {
            location: { name: '&', label: '&', type: 'symbol' },
            subLocation: { name: '&', label: '&', type: 'symbol' },
          };
        }
        return formulaELe;
      });
    }
    copyState = updateFormulastring(finalNewFormulaArray, copyState);
    set(copyState, calculationFormulaPath(pIndex).FORMULA_ARRAY, finalNewFormulaArray);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  const handleEditClick = () => {
    if (allowEditFormula) {
      if (data?.readOnlyFormulaString) {
        const result = splitAndFilterString(data?.readOnlyFormulaString);
        let hasError = false;
        const newFormulaArray = [];
        if (result.length > 0) {
          hasError = handleResultValue(result, newFormulaArray);
        }
        if (hasError) {
          setFormulaHasError(true);
          return;
        }
        if (newFormulaArray.length > 0) {
          handleNewFormulaArray(newFormulaArray);
        }
        setFormulaHasError(false);
      }
    }
    setAllowEditFormula(!allowEditFormula);
  };

  const handleFormulaDirectly = (value) => {
    const copyState = cloneDeep(customColumnState);
    const string = value?.replace(/\s*\[(.*?)_.*?\]/g, '$1');
    set(copyState, calculationFormulaPath(pIndex).READONLY_FORMULA_STRING, string);
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };

  return (
    <>
      <FormGroup className="mb-1">
        <div className="card listing-card mb-0">
          <div className="card-header py-2 px-3">{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.FORMULA_EXPRESSION')}</div>
          <div className="card-body p-2 d-flex position-relative">
            {allowEditFormula ? (
              <MentionsInput
                value={data?.readOnlyFormulaString}
                onChange={(e) => handleFormulaDirectly(e?.target?.value)}
                placeholder={t('CUSTOM_MODULES.LIBRARY.TEMPLATE_COLUMN.NEW_CONTROL.CALCULATION_COMPONENET.READONLY_FORMULA_PLACEHOLDER')}
                className="form-control border-0 rounded-0"
              >
                <Mention
                  markup="[__display__]"
                  trigger="@"
                  data={childOptions}
                  displayTransform={(id, display) => display.split('_(')[0]}
                  renderSuggestion={(suggestion, search, highlightedDisplay) => <div className="user">{highlightedDisplay}</div>}
                />
              </MentionsInput>
            ) : (
              <textarea
                className="form-control border-0 rounded-0"
                name=""
                placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.FORMULA_TEXTAREA_PLACEHOLDER')}
                value={data?.readOnlyFormulaString}
                onChange={(e) => handleFormulaDirectly(e.target.value)}
                readOnly={!allowEditFormula}
              />
            )}
            <button type="button" className="ml-auto btn p-0 position-absolute placement-top-right" onClick={handleEditClick} aria-label="Edit Formula">
              {allowEditFormula ? <i className="icon-Save" /> : <i className="upicon-pen_edit" />}
            </button>
          </div>
          <span className="error-message mt-0 mx-2">{get(globalError, `common[${pIndex}].formulaString`, '')}</span>
        </div>
        {formulaHasError && <span className="error-message align-item-center">{t('CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA')}</span>}
      </FormGroup>
      <div className="d-flex formula-btns">
        {calculationBtnArr?.map((sym) => {
          return (
            <button
              className="btn btn-outline"
              onClick={() => {
                onLocationChange(sym, 'calcSymbol');
              }}
              type="button"
              disabled={(sym.type !== 'number' && sym?.disabled) || allowEditFormula}
              key={sym?.name}
            >
              {sym?.name}
            </button>
          );
        })}
        <button type="button" className="btn btn-default mw-auto px-4 mr-1" onClick={clearFormula}>
          {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.CLEAR')}
        </button>
        <button
          type="button"
          className="btn btn-default mw-auto px-2 mr-1 w-50px"
          aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.UNDO')}
          onClick={undoFormula}
          disabled={(isArray(undo) && undo.length <= 0) || allowEditFormula}
        >
          <span className="icon-undo_icon_new f-16" />
        </button>
        <button
          type="button"
          className="btn btn-default mw-auto px-2 w-50px"
          onClick={redoFormula}
          disabled={(isArray(redo) && redo.length <= 0) || allowEditFormula}
          aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.REDO')}
        >
          <span className="icon-redo_icon_new f-16" />
        </button>
      </div>
      <div className="d-flex mt-2">
        <div className="card listing-card flex-50 mr-1">
          <div className="card-header py-1 px-2 f-14  d-flex align-items-center mh-34">
            {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.LOCATION')}
          </div>
          <div className="card-body p-0">
            <div className="calc-filter-container">
              <input
                type="text"
                className="form-control"
                ref={locationRef}
                onChange={(e) => handleFilter(e.target.value, 'location')}
                disabled={allowEditFormula}
                placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.SEARCH_LOCATION')}
              />
              <button
                type="button"
                onClick={() => clearLocation('location')}
                aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.CLOSE')}
                disabled={allowEditFormula}
              >
                <span className="icon-close" />
              </button>
            </div>

            <ul className="location-ul">
              {filteredLocationArray?.map((loca, ind) => {
                return (
                  <li
                    key={ind}
                    style={(() => {
                      if (state?.currLocation?.name === loca?.name && counterState) {
                        return { background: '#deebff', fontWeight: '400' };
                      }

                      if (!counterState) {
                        return { pointerEvents: 'none', opacity: '0.6' };
                      }

                      return {};
                    })()}
                  >
                    <button type="button" className="btn p-0" onClick={() => onLocationChange(loca, 'location')} disabled={allowEditFormula}>
                      {loca?.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="card listing-card flex-50 ml-1">
          <div className="card-header py-1 px-2 f-14  d-flex align-items-center mh-34">
            {t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.SUB_LOCATIONS')}
          </div>
          <div className="card-body p-0">
            <div className="calc-filter-container">
              <input
                type="text"
                className="form-control"
                ref={subLocationRef}
                onChange={(e) => handleFilter(e.target.value, 'sublocation')}
                disabled={allowEditFormula}
                placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.SEARCH_SUB_LOCATION')}
              />
              <button
                onClick={() => clearLocation('sublocation')}
                type="button"
                aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CALCULATION_COMPONENET.CLOSE')}
                disabled={allowEditFormula}
              >
                <span className="icon-close" />
              </button>
            </div>
            <ul className="location-ul">
              {filteredSubLocationArray?.map((subLoc, ind) => {
                return (
                  <li
                    key={ind}
                    disabled={!counterState}
                    style={(() => {
                      if (state?.currSubLocation?.name === subLoc?.name && counterState) {
                        return { color: 'blue', fontWeight: '400' };
                      }

                      if (!counterState) {
                        return { pointerEvents: 'none', opacity: '0.6' };
                      }

                      return {};
                    })()}
                  >
                    <button type="button" className="btn p-0" onClick={() => onLocationChange(subLoc, 'subLocation')} disabled={allowEditFormula}>
                      {subLoc?.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

export default CalculationComponent;
